/**
 * EventBus - Central event system for decoupled communication
 * 
 * Enables controllers and services to communicate without direct dependencies.
 * Subscribe to events with on(), unsubscribe with off(), emit events with emit().
 * 
 * @module EventBus
 * @see EVENTS for available event constants
 */

/**
 * Event constants for application-wide event bus.
 * Use these constants instead of raw strings to ensure type safety and discoverability.
 * 
 * @namespace EVENTS
 * @example
 * import { eventBus, EVENTS } from './services/EventBus.js';
 * 
 * // Subscribe
 * eventBus.on(EVENTS.DEVICE_ADDED, ({ device, deviceId }) => {
 *     console.log('Device added:', deviceId);
 * });
 * 
 * // Emit
 * eventBus.emit(EVENTS.DEVICE_ADDED, { device: instance, deviceId: instance.id });
 */
export const EVENTS = {
    // ═══════════════════════════════════════════════════════════════════════════
    // DEVICE EVENTS
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Emitted when a new device instance is created on the canvas.
     * @event DEVICE_ADDED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {DeviceInstance} payload.device - The full device instance object
     * @property {string} payload.deviceId - Unique device instance ID
     * @emitter DeviceService.addDevice()
     * @listener LibraryController - Refreshes device counts in library tabs
     */
    DEVICE_ADDED: 'device:added',
    
    /**
     * Emitted when a device instance is removed from the canvas.
     * Associated connections are deleted before this event fires.
     * @event DEVICE_DELETED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {string} payload.deviceId - ID of the deleted device
     * @emitter DeletionController.deleteDevice()
     * @listener LibraryController - Refreshes device counts
     * @listener CableRenderer - Removes orphaned cable endpoints
     */
    DEVICE_DELETED: 'device:deleted',
    
    /**
     * Emitted when a device's layout or properties change (not position).
     * Fired after port additions, column changes, section modifications, etc.
     * @event DEVICE_UPDATED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {string} payload.deviceId - ID of the updated device
     * @emitter InstanceLayoutController - After any layout modification
     * @listener CableRenderer - Re-calculates cable paths (ports may have moved)
     */
    DEVICE_UPDATED: 'device:updated',
    
    // ═══════════════════════════════════════════════════════════════════════════
    // CONNECTION EVENTS
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Emitted when a new cable connection is created between two ports.
     * @event CONNECTION_ADDED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {Connection} payload.connection - The connection object
     * @property {string} payload.connection.id - Unique connection ID
     * @property {Object} payload.connection.from - Source port reference
     * @property {Object} payload.connection.to - Destination port reference
     * @emitter app.js (addConnection)
     * @listener CableRenderer - Renders the new cable, updates port occupancy
     */
    CONNECTION_ADDED: 'connection:added',
    
    /**
     * Emitted when a cable connection is removed.
     * @event CONNECTION_DELETED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {string} payload.connectionId - ID of the deleted connection
     * @emitter DeletionController.deleteConnection()
     * @listener CableRenderer - Removes cable from canvas, frees port occupancy
     */
    CONNECTION_DELETED: 'connection:deleted',
    
    // ═══════════════════════════════════════════════════════════════════════════
    // PATCH EVENTS
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Emitted when a patch is loaded (from storage, file import, or share URL).
     * Fired after canvas is populated with devices and connections.
     * @event PATCH_LOADED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {string} payload.patchName - Name of the loaded patch
     * @emitter StorageUIController.loadPatch(), app.js (share string loading)
     * @listener LibraryController - Refreshes library counts for new devices
     * @listener PatchSwitcherController - Updates current patch display
     */
    PATCH_LOADED: 'patch:loaded',
    
    /**
     * Emitted when a patch is saved to storage (localStorage or cloud).
     * Fired after successful save operation.
     * @event PATCH_SAVED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {string} payload.patchName - Name of the saved patch
     * @emitter StorageUIController.savePatch(), PatchModalController
     * @listener PatchSwitcherController - Updates patch list
     * @listener VersionHistoryController - Creates version snapshot
     */
    PATCH_SAVED: 'patch:saved',
    
    // ═══════════════════════════════════════════════════════════════════════════
    // UI/STATE EVENTS
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Emitted when device/connection selection changes.
     * Reserved for future multi-select UI features.
     * @event SELECTION_CHANGED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {Set<string>} payload.devices - Set of selected device IDs
     * @property {Set<string>} payload.connections - Set of selected connection IDs
     * @emitter (currently unused)
     * @listener (currently unused - reserved for future toolbar/inspector features)
     */
    SELECTION_CHANGED: 'selection:changed',
    
    /**
     * Emitted when the library template list is filtered or updated.
     * Used for internal UI state synchronization.
     * @event LIBRARY_UPDATED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {string} payload.tab - Current library tab ('all'|'audio'|'midi'|'my'|'favorites')
     * @property {number} payload.count - Number of visible templates after filtering
     * @emitter LibraryController - After filtering/tab switch
     * @listener (internal use - future status bar integration)
     */
    LIBRARY_UPDATED: 'library:updated',
    
    /**
     * Emitted when user authentication state changes (login/logout).
     * Fired on initial auth check and after any auth state transition.
     * @event AUTH_CHANGED
     * @type {string}
     * @property {Object} payload - Event payload
     * @property {boolean} payload.isLoggedIn - Whether user is authenticated
     * @property {User|null} payload.user - Supabase user object or null if logged out
     * @emitter app.js (auth state listener)
     * @listener AuthUIController - Updates login/logout button visibility
     * @listener MigrationController - Triggers cloud migration prompt
     * @listener LibraryController - Shows/hides "My Devices" tab
     */
    AUTH_CHANGED: 'auth:changed',
};

/**
 * EventBus class - Simple pub/sub implementation
 */
class EventBus {
    constructor() {
        /** @type {Map<string, Set<Function>>} */
        this.listeners = new Map();
    }

    /**
     * Subscribe to an event
     * @param {string} event - Event name (use EVENTS constants)
     * @param {Function} handler - Callback function(data)
     * @returns {Function} Unsubscribe function for convenience
     */
    on(event, handler) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(handler);
        
        // Return unsubscribe function for convenience
        return () => this.off(event, handler);
    }

    /**
     * Unsubscribe from an event
     * @param {string} event - Event name
     * @param {Function} handler - The handler to remove
     * @returns {boolean} True if handler was removed
     */
    off(event, handler) {
        const handlers = this.listeners.get(event);
        if (handlers) {
            return handlers.delete(handler);
        }
        return false;
    }

    /**
     * Emit an event to all subscribers
     * @param {string} event - Event name
     * @param {*} [data] - Optional data to pass to handlers
     */
    emit(event, data) {
        const handlers = this.listeners.get(event);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(`EventBus: Error in handler for "${event}":`, error);
                }
            });
        }
    }

    /**
     * Subscribe to an event for a single emission only
     * @param {string} event - Event name
     * @param {Function} handler - Callback function(data)
     * @returns {Function} Unsubscribe function
     */
    once(event, handler) {
        const wrappedHandler = (data) => {
            this.off(event, wrappedHandler);
            handler(data);
        };
        return this.on(event, wrappedHandler);
    }

    /**
     * Remove all listeners for an event, or all listeners entirely
     * @param {string} [event] - Optional event name. If omitted, clears all.
     */
    clear(event) {
        if (event) {
            this.listeners.delete(event);
        } else {
            this.listeners.clear();
        }
    }

    /**
     * Get the number of listeners for an event
     * @param {string} event - Event name
     * @returns {number} Number of listeners
     */
    listenerCount(event) {
        const handlers = this.listeners.get(event);
        return handlers ? handlers.size : 0;
    }
}

// Export singleton instance
export const eventBus = new EventBus();
